import { FC, useEffect, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import {
	emptyOrganizationInfo,
	listApprovedOrganizations,
	OrganizationInfo,
	OrganizationSummary,
} from "../../api/organizations";
import { OrganizationFields } from "./OrganizationFields";

/** Either an existing approved organization, or a new one suggested for admin approval. */
export type OrganizationChoice =
	| { kind: "existing"; id: number }
	| { kind: "new"; info: OrganizationInfo }
	| null;

export const isOrganizationChoiceValid = (choice: OrganizationChoice) =>
	choice !== null &&
	(choice.kind === "existing" || choice.info.name.trim().length > 0);

const SUGGEST_NEW = { id: -1, name: "+ Suggest a new organization" } as OrganizationSummary;

type Props = {
	value: OrganizationChoice;
	onChange: (value: OrganizationChoice) => void;
};

/** Signup-page control for Contractor / LPA users to pick or suggest their organization. */
export const OrganizationPicker: FC<Props> = ({ value, onChange }) => {
	const [options, setOptions] = useState<OrganizationSummary[]>([]);

	useEffect(() => {
		listApprovedOrganizations()
			.then(setOptions)
			.catch((e) => console.error(e));
	}, []);

	const selected =
		value?.kind === "existing"
			? (options.find((o) => o.id === value.id) ?? null)
			: value?.kind === "new"
				? SUGGEST_NEW
				: null;

	return (
		<Box sx={{ mt: "24px" }}>
			<Typography fontSize={16} fontWeight={600}>
				Your organization
			</Typography>
			<Autocomplete
				sx={{ mt: "12px" }}
				options={[...options, SUGGEST_NEW]}
				value={selected}
				getOptionLabel={(o) => o.name}
				isOptionEqualToValue={(a, b) => a.id === b.id}
				// keep "suggest new" visible regardless of what's typed
				filterOptions={(opts, { inputValue }) =>
					opts.filter(
						(o) =>
							o.id === SUGGEST_NEW.id ||
							o.name.toLowerCase().includes(inputValue.toLowerCase()),
					)
				}
				onChange={(_, org) => {
					if (!org) onChange(null);
					else if (org.id === SUGGEST_NEW.id)
						onChange({ kind: "new", info: emptyOrganizationInfo });
					else onChange({ kind: "existing", id: org.id });
				}}
				renderInput={(params) => (
					<TextField {...params} placeholder="Select organization" />
				)}
			/>
			{value?.kind === "new" && (
				<Box sx={{ mt: "16px" }}>
					<Typography fontSize={14} color="gray" sx={{ mb: "8px" }}>
						New organizations are reviewed by an administrator along with your
						account.
					</Typography>
					<OrganizationFields
						value={value.info}
						onChange={(info) => onChange({ kind: "new", info })}
					/>
				</Box>
			)}
		</Box>
	);
};
